import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { getAuthStatus, verifyToken, getStoredToken, setStoredToken, clearStoredToken } from '@/services/api'

interface AuthContextType {
  isAuthenticated: boolean
  token: string | null
  telegramUsername: string | null
  telegramUserId: number | null
  login: (token: string) => Promise<boolean>
  logout: () => void
  isLoading: boolean
  authEnabled: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [telegramUsername, setTelegramUsername] = useState<string | null>(null)
  const [telegramUserId, setTelegramUserId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authEnabled, setAuthEnabled] = useState(false)

  // Check auth status on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        const storedToken = getStoredToken()

        // Check server auth status
        const status = await getAuthStatus()
        setAuthEnabled(status.auth_enabled)

        if (!status.auth_enabled) {
          // Auth is disabled, allow access
          setIsAuthenticated(true)
          setIsLoading(false)
          return
        }

        if (status.authenticated && storedToken) {
          // Already authenticated
          setIsAuthenticated(true)
          setToken(storedToken)
          setTelegramUsername(status.telegram_username || null)
          setTelegramUserId(status.telegram_user_id || null)
        } else {
          // Not authenticated
          setIsAuthenticated(false)
          clearStoredToken()
        }
      } catch (error) {
        console.error('Failed to check auth status:', error)
        // Fail closed: a network blip shouldn't silently grant access.
        // Require login rather than assuming the server has auth disabled.
        setIsAuthenticated(false)
        setAuthEnabled(true)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = useCallback(async (newToken: string): Promise<boolean> => {
    try {
      const result = await verifyToken(newToken)

      if (result.valid) {
        setStoredToken(newToken)
        setToken(newToken)
        setIsAuthenticated(true)
        setTelegramUsername(result.telegram_username || null)
        setTelegramUserId(result.telegram_user_id || null)
        return true
      }

      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }, [])

  const logout = useCallback(() => {
    clearStoredToken()
    setToken(null)
    setIsAuthenticated(false)
    setTelegramUsername(null)
    setTelegramUserId(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        telegramUsername,
        telegramUserId,
        login,
        logout,
        isLoading,
        authEnabled,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
